/**
 * ErrorLogViewer - Owner/Admin Dashboard for API Error Monitoring
 * 
 * Features:
 * - View recent API errors with filtering
 * - Error summary statistics
 * - Mark errors as resolved with notes
 * - Pagination support
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import { api } from '../utils/api';
import { 
  AlertTriangle, CheckCircle, XCircle, RefreshCw, Filter, 
  ChevronDown, ChevronUp, ExternalLink, Clock, Server,
  TrendingUp, AlertOctagon, Check, X, MessageSquare
} from 'lucide-react';

interface ErrorLog {
  id?: string;
  status: number;
  message: string;
  endpoint: string;
  timestamp: string;
  details?: any;
  user_agent?: string;
  url?: string;
  user_email?: string;
  org_id?: string;
  resolved: boolean;
  resolved_at?: string;
  resolved_by?: string;
  notes?: string;
  created_at?: string;
}

interface ErrorSummary {
  total_errors: number;
  unresolved_errors: number;
  resolved_errors: number;
  status_breakdown: Array<{ status: number; count: number }>;
  top_endpoints: Array<{ endpoint: string; count: number }>;
  recent_errors: ErrorLog[];
  period_days: number;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ErrorLogViewer() {
  const { getToken } = useAuth();
  
  // State
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [summary, setSummary] = useState<ErrorSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<number | null>(null);
  const [resolvedFilter, setResolvedFilter] = useState<boolean | null>(null);
  const [daysFilter, setDaysFilter] = useState(7);
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Expanded error details
  const [expandedId, setExpandedId] = useState<string | null>(null);
  
  // Resolve modal
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');

  // Fetch error summary
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const token = await getToken();
      const data = await api.get(
        `/api/v1/errors/summary?days=${daysFilter}`,
        { headers: { Authorization: `Bearer ${token}` }, showErrorToast: false }
      );
      setSummary(data);
    } catch (err: any) {
      console.error('Failed to fetch summary:', err);
      if (err.status === 403) {
        setError('You do not have permission to view error logs');
      }
    } finally {
      setSummaryLoading(false);
    }
  }, [getToken, daysFilter]);

  // Fetch error list
  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      
      let url = `/api/v1/errors/list?limit=${limit}&offset=${offset}&days=${daysFilter}`;
      if (statusFilter !== null) url += `&status_filter=${statusFilter}`;
      if (resolvedFilter !== null) url += `&resolved=${resolvedFilter}`;
      
      const data = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      
      setErrors(data.errors || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch errors:', err);
      if (err.status === 403) {
        setError('You do not have permission to view error logs');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [getToken, offset, daysFilter, statusFilter, resolvedFilter]);

  // Resolve error
  const resolveError = async (errorId: string) => {
    try {
      const token = await getToken();
      await api.put(
        `/api/v1/errors/${errorId}/resolve`,
        { notes: resolveNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh data
      fetchErrors();
      fetchSummary();
      setResolvingId(null);
      setResolveNotes('');
    } catch (err) {
      console.error('Failed to resolve error:', err);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchSummary();
    fetchErrors();
  }, [fetchSummary, fetchErrors]);

  // Get status color
  const getStatusColor = (status: number): React.CSSProperties => {
    if (status >= 500) return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    if (status >= 400) return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (error && !errors.length) {
    return (
      <div className="border rounded-xl p-6 text-center" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <XCircle className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-needs-human-text)' }} />
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Access Denied</h3>
        <p style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{summary.total_errors}</p>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Total Errors ({summary.period_days}d)</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                <AlertOctagon className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{summary.unresolved_errors}</p>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Unresolved</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'transparent' }}>
                <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-verified)' }}>{summary.resolved_errors}</p>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Resolved</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                <Server className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{summary.top_endpoints.length}</p>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Affected Endpoints</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Breakdown */}
      {summary && summary.status_breakdown.length > 0 && (
        <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <h3 className="font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>Error Distribution by Status</h3>
          <div className="flex flex-wrap gap-2">
            {summary.status_breakdown.map(({ status, count }) => (
              <button
                key={status}
                onClick={() => setStatusFilter(statusFilter === status ? null : status)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border transition"
                style={
                  statusFilter === status
                    ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', borderColor: 'var(--color-brand)' }
                    : getStatusColor(status)
                }
              >
                {status}: {count}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Filters:</span>
          </div>

          <select
            value={daysFilter}
            onChange={(e) => { setDaysFilter(Number(e.target.value)); setOffset(0); }}
            className="px-3 py-1.5 border rounded-lg text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>

          <select
            value={resolvedFilter === null ? '' : String(resolvedFilter)}
            onChange={(e) => {
              setResolvedFilter(e.target.value === '' ? null : e.target.value === 'true');
              setOffset(0);
            }}
            className="px-3 py-1.5 border rounded-lg text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <option value="">All Status</option>
            <option value="false">Unresolved</option>
            <option value="true">Resolved</option>
          </select>

          {statusFilter && (
            <button
              onClick={() => setStatusFilter(null)}
              className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"
              style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            >
              Status: {statusFilter} <X className="w-3 h-3" />
            </button>
          )}

          <button
            onClick={() => { fetchErrors(); fetchSummary(); }}
            className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ml-auto"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error List */}
      <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-base)' }}>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Error Log</h3>
            <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {offset + 1}-{Math.min(offset + limit, total)} of {total}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" style={{ color: 'var(--color-needs-human-text)' }} />
            <p style={{ color: 'var(--color-text-muted)' }}>Loading errors...</p>
          </div>
        ) : errors.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-verified)' }} />
            <h3 className="text-lg font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>No Errors Found</h3>
            <p style={{ color: 'var(--color-text-muted)' }}>Great! No API errors in the selected period.</p>
          </div>
        ) : (
          <div className="divide-y divide-[color:var(--color-border-faint)]">
            {errors.map((err, idx) => (
              <div key={err.id || idx} className="transition">
                {/* Error Row */}
                <div 
                  className="px-4 py-3 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === (err.id || String(idx)) ? null : (err.id || String(idx)))}
                >
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 rounded text-xs font-bold" style={getStatusColor(err.status)}>
                      {err.status}
                    </span>

                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{err.endpoint}</p>
                      <p className="text-sm truncate" style={{ color: 'var(--color-text-muted)' }}>{err.message}</p>
                    </div>

                    <div className="flex items-center gap-2">
                      {err.resolved ? (
                        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ color: 'var(--color-verified)', backgroundColor: 'transparent' }}>
                          <CheckCircle className="w-3 h-3" /> Resolved
                        </span>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setResolvingId(err.id || String(idx)); }}
                          className="px-2 py-1 text-xs rounded"
                          style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                        >
                          Resolve
                        </button>
                      )}

                      <span className="text-xs whitespace-nowrap" style={{ color: 'var(--color-text-dim)' }}>
                        {formatDate(err.timestamp || err.created_at || '')}
                      </span>

                      {expandedId === (err.id || String(idx)) ? (
                        <ChevronUp className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                      ) : (
                        <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedId === (err.id || String(idx)) && (
                  <div className="px-4 py-3 border-t" style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border-faint)' }}>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="mb-1" style={{ color: 'var(--color-text-muted)' }}>User</p>
                        <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{err.user_email || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="mb-1" style={{ color: 'var(--color-text-muted)' }}>Page URL</p>
                        <p className="font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{err.url || 'N/A'}</p>
                      </div>
                      {err.details && (
                        <div className="col-span-2">
                          <p className="mb-1" style={{ color: 'var(--color-text-muted)' }}>Error Details</p>
                          <pre className="rounded p-2 text-xs overflow-x-auto" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                            {JSON.stringify(err.details, null, 2)}
                          </pre>
                        </div>
                      )}
                      {err.resolved && err.notes && (
                        <div className="col-span-2">
                          <p className="mb-1" style={{ color: 'var(--color-text-muted)' }}>Resolution Notes</p>
                          <p className="border rounded p-2" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)', color: 'var(--color-verified)' }}>
                            {err.notes}
                          </p>
                          <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
                            Resolved by {err.resolved_by} on {formatDate(err.resolved_at || '')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Resolve Modal */}
                {resolvingId === (err.id || String(idx)) && (
                  <div className="px-4 py-3 border-t" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                    <div className="flex items-start gap-3">
                      <MessageSquare className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
                      <div className="flex-1">
                        <p className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Mark as Resolved</p>
                        <textarea
                          value={resolveNotes}
                          onChange={(e) => setResolveNotes(e.target.value)}
                          placeholder="Add notes about the fix (optional)..."
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                          style={{ borderColor: 'var(--color-needs-human-tint-bd)' }}
                          rows={2}
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => resolveError(err.id || String(idx))}
                            className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"
                            style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                          >
                            <Check className="w-4 h-4" /> Confirm
                          </button>
                          <button
                            onClick={() => { setResolvingId(null); setResolveNotes(''); }}
                            className="px-3 py-1.5 rounded-lg text-sm"
                            style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {total > limit && (
          <div className="px-4 py-3 border-t flex items-center justify-between" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-base)' }}>
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
            >
              ← Previous
            </button>
            <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
            </span>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
              className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
